import { useTranslation } from "react-i18next";
import { DeviceFC } from "types";

import { ProductCardFooterItem } from "@/components/Layout/BaseLayouts/ProductCard/ProductCardFooterItem";
import { ExtendedSimCardDetail } from "@/hooks/useExtendedSims";
import {
  getInternationalCallsTileDisplay,
  getProductIdFromSim,
} from "@/utils/productMatrix";

export const CallsInfo: DeviceFC = ({
  device,
}: {
  device: ExtendedSimCardDetail;
}) => {
  const { t } = useTranslation();

  const display = getInternationalCallsTileDisplay({
    productId: getProductIdFromSim(device),
    creditClass: device.creditClass,
  });

  if (display.kind === "hidden") {
    return null;
  }

  const label = t("layout.tiles.activeSim.internationalCalls");

  if (display.kind === "notAllowed") {
    return (
      <ProductCardFooterItem
        variant="progressbar"
        label={label}
        value={0}
        max={1}
        description={t("layout.tiles.activeSim.notAvailable")}
      />
    );
  }

  if (display.kind === "unlimited") {
    return (
      <ProductCardFooterItem
        variant="progressbar"
        label={label}
        value={1}
        max={1}
        description={t("layout.tiles.activeSim.unlimited")}
      />
    );
  }

  // The known, static allowance from the product config -- more reliable
  // than the live response's maxInclRoamingCalls, which can be absent.
  const maxInclRoamingCallsMin = display.allowanceSec / 60;
  const roamingCallsMin = device.roamingCallPackageUsage?.roamingCalls
    ? Math.ceil(device.roamingCallPackageUsage.roamingCalls / 60)
    : 0;
  // Remaining minutes (full when unused), matching data usage and the
  // roaming tab's "available/verfügbar" framing — not spent/used.
  const remainingCallsMin = Math.max(
    0,
    maxInclRoamingCallsMin - roamingCallsMin,
  );

  return (
    <ProductCardFooterItem
      variant="progressbar"
      label={label}
      value={remainingCallsMin}
      max={maxInclRoamingCallsMin}
      description={t("layout.tiles.activeSim.roamingCallUsage", {
        remaining: remainingCallsMin,
        available: maxInclRoamingCallsMin,
      })}
    />
  );
};
